import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useLoginMutation } from '../services/authApi';
import { useAppDispatch } from '../store/hooks';
import { loginSuccess } from '../store/slices/authSlice';
import { APPLICATION_LOCAL_STORAGE } from '../common/constants/constants';
import { getEnv } from '../config/environment';
import type {
  LoginResponse,
  LoginResponseData,
  UserPrivilege,
  AllowedRouteItem,
  ApiErrorResponse,
} from '../types/auth.types';
import idLogo from '../assets/idLogo.png';
import WDayLogo from '../components/common/WDayLogo';
import '../styles/signin.css';

const allowedRolesArray: AllowedRouteItem[] = [
  { privilege: 'EMPLOYEE_MASTER_DASHBOARD1', route: '/employee-master/dashboard' },
  { privilege: 'EMPLOYEE_MASTER_SNAPSHOT', route: '/employee-master/snapshot' },
  { privilege: 'AD_MANAGER_SNAPSHOT', route: '/employee-master/snapshot' },
  { privilege: 'EMPLOYEE_MASTER_SNAPSHOT_LOGS', route: '/employee-master/logs' },
  { privilege: 'AD_MANAGER_ACCESS_LOGS', route: '/employee-master/logs' },
  { privilege: 'AP_PROHANCE', route: '/auto-provisioning/prohance' },
  { privilege: 'AP_GLOBAL_JOB', route: '/auto-provisioning/manage-jobs' },
  { privilege: 'AP_PIXIE', route: '/auto-provisioning/configure' },
  { privilege: 'FALCON_LOGS', route: '/falcon/logs' },
];

const { ping, manualLogin } = getEnv();

const CONTROL_H = 'h-9';
const INPUT_CLASS = `${CONTROL_H} w-full rounded-lg border border-gray-300 bg-white px-3.5 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/30`;
const BTN_BASE = `${CONTROL_H} w-full flex items-center justify-center rounded-lg text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70`;

const processedPingTokens = new Set<string>();

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [login, { isLoading }] = useLoginMutation();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  // const [rememberMe, setRememberMe] = useState(false);
  const [ip, setIp] = useState<string>('');
  const [pingToken, setPingToken] = useState<string | undefined>();
  const [pingSubmit, setPingSubmit] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    isError: boolean;
  }>({ open: false, message: '', isError: true });

  useEffect(() => {
    fetch('https://api.ipify.org/?format=json')
      .then((res) => res.json())
      .then((data: { ip?: string }) => {
        if (data.ip) setIp(data.ip);
        else setIp('unknown');
      })
      .catch((err) => {
        console.error('Failed to fetch IP', err);
        setIp('unknown');
      });
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem(APPLICATION_LOCAL_STORAGE.PING_ACCESS_TOKEN);
    console.log(
      'SignIn.tsx: storedToken from localStorage is:',
      storedToken ? storedToken.substring(0, 15) + '...' : 'null'
    );
    if (storedToken) {
      setPingToken(storedToken);
    } else {
      const url = window.location.href;
      console.log('SignIn.tsx: storedToken is null. Checking current URL for token:', url);
      const match = url?.match(/(?:#access[-_]token)=([^&]+)/);
      if (match && match[1]) {
        console.log(
          'SignIn.tsx: Found token in URL, storing and setting:',
          match[1].substring(0, 15) + '...'
        );
        localStorage.setItem(APPLICATION_LOCAL_STORAGE.PING_ACCESS_TOKEN, match[1]);
        setPingToken(match[1]);
      } else {
        console.log('SignIn.tsx: No token found in URL matching regex.');
      }
    }
  }, []);

  useEffect(() => {
    if (!snackbar.open) return;

    const timer = setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, open: false }));
    }, 10000);

    return () => clearTimeout(timer);
  }, [snackbar.open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLoginSuccess = useCallback(
    (result: LoginResponse) => {
      const responseData: LoginResponse | LoginResponseData = result?.data || result;

      const privileges: UserPrivilege[] = responseData?.privileges || [];
      const sessionTimeoutInMins = responseData?.sessionTimeoutInMins || 30;

      const authToken = result.authToken || responseData.authToken || '';
      const refreshToken = result.refreshToken || responseData.refreshToken || '';

      localStorage.setItem(APPLICATION_LOCAL_STORAGE.AUTH_TOKEN, authToken);
      localStorage.setItem(APPLICATION_LOCAL_STORAGE.REFRESH_TOKEN, refreshToken);
      localStorage.setItem(APPLICATION_LOCAL_STORAGE.TOKEN_TIME_STAMP, Date.now().toString());
      localStorage.setItem(
        APPLICATION_LOCAL_STORAGE.SESSION_TIMEOUT_IN_MILLI_SECONDS,
        (sessionTimeoutInMins * 60000).toString()
      );

      const userId = 'userId' in responseData ? responseData.userId : undefined;
      const id = 'id' in responseData ? responseData.id : null;
      const employeeId = 'employeeId' in responseData ? responseData.employeeId : userId;

      dispatch(
        loginSuccess({
          id: userId != null ? String(userId) : id != null ? String(id) : null,
          employeeId:
            employeeId != null ? String(employeeId) : userId != null ? String(userId) : undefined,
          firstName: responseData?.firstName,
          lastName: responseData?.lastName,
          position: responseData?.position,
          authToken,
          refreshToken,
          privileges,
          segments: responseData?.segments,
        })
      );

      const defaultRouteItem = allowedRolesArray.find((item) =>
        privileges.find((p: UserPrivilege) => p.type === item.privilege && p.privilege)
      );

      const route = defaultRouteItem?.route;
      const isValidLink = allowedRolesArray.some((item) => item.route === route);

      if (isValidLink && route) {
        navigate(route);
      } else {
        navigate('/employee-master/snapshot');
      }
    },
    [dispatch, navigate]
  );

  const onSubmitWithPing = useCallback(
    async (token: string) => {
      console.log(
        'SignIn.tsx: onSubmitWithPing called with token:',
        token.substring(0, 15) + '...'
      );
      setPingSubmit(true);
      try {
        const authToken = token.split('&token_type=')[0];
        const payload = { authToken, ip };
        console.log('SignIn.tsx: Sending login payload:', payload);
        const result = await login(payload).unwrap();
        console.log('SignIn.tsx: Login API succeeded. Result:', result);
        handleLoginSuccess(result);
      } catch (err: unknown) {
        console.error('SignIn.tsx: Login API failed. Error:', err);
        processedPingTokens.delete(token);
        const error = err as ApiErrorResponse;
        const errorMsg = error?.data?.message ?? 'PingID Sign In failed.';
        setSnackbar({ open: true, message: errorMsg, isError: true });
        setPingSubmit(false);
      }
    },
    [login, ip, handleLoginSuccess]
  );

  useEffect(() => {
    if (pingToken && !processedPingTokens.has(pingToken) && ip) {
      processedPingTokens.add(pingToken);
      onSubmitWithPing(pingToken);
    }
  }, [pingToken, onSubmitWithPing, ip]);

  const authViaPing = async () => {
    setPingSubmit(true);
    window.open(
      `${ping.auth_url}/${ping.env_id}/as/authorize?client_id=${ping.client_id}&redirect_uri=${encodeURIComponent(ping.redirect_uri)}&response_type=token&scope=openid+profile+email`,
      '_self'
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        userName: form.username,
        userPassword: form.password,
        ip: ip,
      };
      const result = await login(payload).unwrap();
      handleLoginSuccess(result);
    } catch (err: unknown) {
      const error = err as ApiErrorResponse;
      const errorMsg = error?.data?.message ?? 'Sign In failed. Please try again.';
      setSnackbar({ open: true, message: errorMsg, isError: true });
    }
  };

  const isBusy = isLoading || pingSubmit;

  return (
    <div className="signin-container">
      <div className="signin-wrapper">
        {/* Left Side - Illustration */}
        <svg
          className="signin-illustration"
          viewBox="0 0 1200 700"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Background gradient circle */}
          <defs>
            <linearGradient id="gradientBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#0318a1', stopOpacity: 0.1 }} />
              <stop offset="100%" style={{ stopColor: '#7f8ffd', stopOpacity: 0.2 }} />
            </linearGradient>
            <linearGradient id="lockGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#ff006b', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#fc79af', stopOpacity: 1 }} />
            </linearGradient>
            <linearGradient id="phoneGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#ffd800', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#fce566', stopOpacity: 1 }} />
            </linearGradient>
            <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#0318a1', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#7f8ffd', stopOpacity: 1 }} />
            </linearGradient>
          </defs>

          {/* Decorative circles */}
          <circle cx="320" cy="80" r="60" fill="url(#gradientBg)" opacity="0.6" />
          <circle cx="80" cy="380" r="50" fill="url(#gradientBg)" opacity="0.5" />
          <circle cx="350" cy="450" r="45" fill="url(#gradientBg)" opacity="0.4" />
          <circle cx="920" cy="80" r="60" fill="url(#gradientBg)" opacity="0.6" />
          <circle cx="980" cy="300" r="50" fill="url(#gradientBg)" opacity="0.5" />
          <circle cx="950" cy="450" r="45" fill="url(#gradientBg)" opacity="0.4" />

          {/* Lock Icon */}
          <g transform="translate(200, 600)">
            <circle cx="0" cy="-5" r="45" fill="url(#lockGradient)" opacity="0.9" />
            <path
              d="M -15 -5 L -15 -20 Q -15 -30 -5 -30 L 5 -30 Q 15 -30 15 -20 L 15 -5 M -15 -5 L 15 -5 L 15 15 Q 15 20 10 20 L -10 20 Q -15 20 -15 15 Z"
              fill="white"
              stroke="none"
            />
            <circle cx="0" cy="5" r="3" fill="white" />
          </g>
          {/* Lock Icon */}
          <g transform="translate(750, 550)">
            <circle cx="0" cy="-5" r="45" fill="url(#lockGradient)" opacity="0.9" />
            <path
              d="M -15 -5 L -15 -20 Q -15 -30 -5 -30 L 5 -30 Q 15 -30 15 -20 L 15 -5 M -15 -5 L 15 -5 L 15 15 Q 15 20 10 20 L -10 20 Q -15 20 -15 15 Z"
              fill="white"
              stroke="none"
            />
            <circle cx="0" cy="5" r="3" fill="white" />
          </g>

          {/* Device/Phone Icon */}
          <g transform="translate(280, 200)">
            <circle cx="0" cy="0" r="50" fill="url(#phoneGradient)" opacity="0.85" />
            <rect x="-20" y="-30" width="40" height="60" rx="5" fill="white" opacity="0.3" />
            <rect x="-18" y="-25" width="36" height="50" rx="3" fill="white" opacity="0.5" />
            <circle cx="0" cy="27" r="2" fill="white" opacity="0.8" />
          </g>
          <g transform="translate(1100, 200)">
            <circle cx="0" cy="0" r="50" fill="url(#phoneGradient)" opacity="0.85" />
            <rect x="-20" y="-30" width="40" height="60" rx="5" fill="white" opacity="0.3" />
            <rect x="-18" y="-25" width="36" height="50" rx="3" fill="white" opacity="0.5" />
            <circle cx="0" cy="27" r="2" fill="white" opacity="0.8" />
          </g>

          {/* User/Person Icon */}
          <g transform="translate(120, 380)">
            <circle cx="0" cy="-15" r="12" fill="#7f8ffd" />
            <path
              d="M -20 5 Q -20 -5 -10 -5 L 10 -5 Q 20 -5 20 5 L 20 25 L -20 25 Z"
              fill="#7f8ffd"
            />
          </g>
          <g transform="translate(920, 380)">
            <circle cx="0" cy="-15" r="12" fill="#7f8ffd" />
            <path
              d="M -20 5 Q -20 -5 -10 -5 L 10 -5 Q 20 -5 20 5 L 20 25 L -20 25 Z"
              fill="#7f8ffd"
            />
          </g>

          {/* Shield Icon */}
          <g transform="translate(200, 300)">
            <circle cx="0" cy="-6" r="35" fill="url(#shieldGradient)" opacity="0.8" />
            <path
              d="M -12 -15 L -12 -5 Q -12 5 0 10 Q 12 5 12 -5 L 12 -15 Z"
              fill="white"
              opacity="0.9"
            />
            <path d="M -2 0 L 2 0 L 2 5 L -2 5 Z" fill="url(#shieldGradient)" />
          </g>
          <g transform="translate(750, 200)">
            <circle cx="0" cy="-6" r="35" fill="url(#shieldGradient)" opacity="0.8" />
            <path
              d="M -12 -15 L -12 -5 Q -12 5 0 10 Q 12 5 12 -5 L 12 -15 Z"
              fill="white"
              opacity="0.9"
            />
            <path d="M -2 0 L 2 0 L 2 5 L -2 5 Z" fill="url(#shieldGradient)" />
          </g>

          {/* Decorative bars */}
          <rect x="250" y="350" width="8" height="60" rx="4" fill="#0318a1" opacity="0.3" />
          <rect x="265" y="340" width="8" height="70" rx="4" fill="#0318a1" opacity="0.4" />
          <rect x="280" y="360" width="8" height="50" rx="4" fill="#0318a1" opacity="0.3" />

          <rect x="950" y="550" width="8" height="60" rx="4" fill="#0318a1" opacity="0.3" />
          <rect x="965" y="540" width="8" height="70" rx="4" fill="#0318a1" opacity="0.4" />
          <rect x="980" y="560" width="8" height="50" rx="4" fill="#0318a1" opacity="0.3" />

          {/* Floating circles */}
          <circle cx="150" cy="50" r="6" fill="#7f8ffd" opacity="0.6" />
          <circle cx="320" cy="420" r="5" fill="#0318a1" opacity="0.5" />
          <circle cx="60" cy="220" r="7" fill="#7f8ffd" opacity="0.4" />

          <circle cx="50" cy="50" r="6" fill="#0318a1" opacity="0.6" />
          <circle cx="220" cy="220" r="5" fill="#7f8ffd" opacity="0.5" />
          <circle cx="260" cy="300" r="7" fill="#0318a1" opacity="0.4" />

          <circle cx="950" cy="50" r="6" fill="#7f8ffd" opacity="0.6" />
          <circle cx="1020" cy="420" r="5" fill="#0318a1" opacity="0.5" />
          <circle cx="1200" cy="220" r="7" fill="#7f8ffd" opacity="0.4" />

          <circle cx="750" cy="50" r="6" fill="#0318a1" opacity="0.6" />
          <circle cx="820" cy="220" r="5" fill="#7f8ffd" opacity="0.5" />
          <circle cx="860" cy="300" r="7" fill="#0318a1" opacity="0.4" />

          {/* Decorative curves */}
          <g transform="translate(0, 500) rotate(-10)">
            <path
              d="M 0 0 Q 50 -30 100 0 T 200 0"
              stroke="#0318a1"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M 20 20 Q 70 -10 120 20 T 220 20"
              stroke="#7f8ffd"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
          </g>
          <g transform="translate(600, 600) rotate(15)">
            <path
              d="M 0 0 Q 50 -30 100 0 T 200 0"
              stroke="#0318a1"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M 20 20 Q 70 -10 120 20 T 220 20"
              stroke="#7f8ffd"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
          </g>
          <path
            d="M 70 170 Q 120 140 170 170"
            stroke="#0318a1"
            strokeWidth="2"
            fill="none"
            opacity="0.3"
          />
          <path
            d="M 280 320 Q 320 300 350 330"
            stroke="#7f8ffd"
            strokeWidth="2"
            fill="none"
            opacity="0.3"
          />
        </svg>
        {/* <div className="signin-left-panel">
                </div> */}

        {/* Right Side - Sign In Form */}
        <div className="signin-right-panel">
          <div className="signin-form-container">
            {/* Header */}
            <div className="signin-header">
              <WDayLogo style={{ height: '36px' }} />
            </div>

            {/* PingID Sign In Button */}
            <button
              type="button"
              disabled={isBusy}
              onClick={authViaPing}
              className={`${BTN_BASE} mt-5 mb-5 gap-1 border-0 bg-[#BC1C24] text-white shadow-sm hover:bg-[#a3181f]`}
            >
              {pingSubmit ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <>
                  Sign in with Ping
                  <img src={idLogo} alt="" className="ml-0.5 h-[18px] w-[18px]" />
                </>
              )}
            </button>
            {manualLogin && (
              <>
                {/* Divider */}
                <div className="signin-divider">
                  <span className="signin-divider-line" />
                  <span className="signin-divider-text">OR</span>
                  <span className="signin-divider-line" />
                </div>

                {/* Sign In Form */}
                <form onSubmit={handleSubmit} className="signin-form">
                  {/* Email/Username Field */}
                  <div className="signin-form-group">
                    <label htmlFor="signin-email" className="signin-label">
                      Username
                    </label>
                    <div className="signin-input-wrapper">
                      <input
                        id="signin-email"
                        name="username"
                        type="text"
                        value={form.username}
                        onChange={handleChange}
                        autoComplete="username"
                        placeholder="Enter your username"
                        className={INPUT_CLASS}
                        disabled={isBusy}
                      />
                      <span className="signin-input-icon signin-email-icon" />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="signin-form-group">
                    <label htmlFor="signin-password" className="signin-label">
                      Password
                    </label>
                    <div className="signin-input-wrapper">
                      <input
                        id="signin-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={handleChange}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        className={INPUT_CLASS}
                        disabled={isBusy}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="signin-toggle-password"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <span className="signin-eye-icon signin-eye-closed" />
                        ) : (
                          <span className="signin-eye-icon signin-eye-open" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isBusy}
                    className={`${BTN_BASE} signin-submit-btn`}
                  >
                    {isLoading ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Snackbar/Toast Notification */}
      {snackbar.open && (
        <div
          role="alert"
          className={`signin-snackbar ${snackbar.isError ? 'signin-snackbar-error' : 'signin-snackbar-success'}`}
        >
          {snackbar.isError ? (
            <svg
              className="signin-snackbar-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              className="signin-snackbar-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          )}
          <span className="signin-snackbar-text">{snackbar.message}</span>
        </div>
      )}
    </div>
  );
};

export default SignIn;
